import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import type { ResolvedMenuGroup, ResolvedMenuItem, ResolvedMenuColumn } from '../../plugins/types';
import { filterMenuBySearch } from '../../plugins/dynamicNav';
import { rankLauncherResults } from '../launcher/rankLauncherResults';
import { trackMegaMenuOutsideResult } from '../launcher/launcherTelemetry';
import type { LauncherSearchItem } from '../launcher/types';
import { ExperienceIcon, isRegistryKey } from '../icons';
import { Search, Check, X } from 'lucide-react';
import './mega-menu.css';

// =============================================================================
// MEGA MENU COMPONENTS
// Per Blue Steel: Magnum-level excellence with config-driven architecture
// =============================================================================

/**
 * Fuzzy search scoring function
 * Magnum: Better matching than simple includes()
 */
function fuzzyMatch(text: string, query: string): number {
  const textLower = text.toLowerCase();
  const queryLower = query.toLowerCase();
  
  // Exact match scores highest
  if (textLower === queryLower) return 100;
  
  // Starts with scores high
  if (textLower.startsWith(queryLower)) return 90;
  
  // Contains as substring scores medium
  if (textLower.includes(queryLower)) return 70;
  
  // Fuzzy character matching
  let score = 0;
  let queryIdx = 0;
  let consecutive = 0;
  
  for (let i = 0; i < textLower.length && queryIdx < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIdx]) {
      score += 10 + consecutive * 5; // Bonus for consecutive matches
      consecutive++;
      queryIdx++;
    } else {
      consecutive = 0;
    }
  }
  
  // All query chars must be found
  return queryIdx === queryLower.length ? score : 0;
}

/**
 * Filter items with fuzzy matching and sort by relevance
 * Magnum: Intelligent search that "just works"
 */
function fuzzyFilterItems(items: ResolvedMenuItem[], query: string): ResolvedMenuItem[] {
  if (!query.trim()) return items;
  
  return items
    .map(item => ({
      item,
      score: Math.max(
        fuzzyMatch(item.label, query),
        fuzzyMatch(item.id, query),
        item.description ? fuzzyMatch(item.description, query) * 0.7 : 0
      )
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item);
}

interface MegaMenuTriggerProps {
  label: string;
  icon?: string;
  isOpen: boolean;
  onClick: () => void;
}

/**
 * MegaMenuTrigger - Button in nav bar that opens mega menu
 */
export const MegaMenuTrigger = React.forwardRef<HTMLButtonElement, MegaMenuTriggerProps>(
  ({ label, icon, isOpen, onClick }, ref) => {
    // Blue Steel: All colors via CSS variables, zero hardcoded values
    const triggerClasses = ['mega-menu-trigger', isOpen ? 'active' : ''].filter(Boolean).join(' ');
    
    return (
      <button
        ref={ref}
        onClick={onClick}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={triggerClasses}
      >
        {label}
        <span className={`mega-menu-trigger-chevron ${isOpen ? 'open' : ''}`}>▾</span>
      </button>
    );
  }
);

MegaMenuTrigger.displayName = 'MegaMenuTrigger';

// =============================================================================
// MEGA MENU ITEM
// =============================================================================

interface MegaMenuItemProps {
  item: ResolvedMenuItem;
  isFocused: boolean;
  onItemClick: (item: ResolvedMenuItem) => void;
  onFocus: () => void;
}

function MegaMenuItem({ item, isFocused, onItemClick, onFocus }: MegaMenuItemProps) {
  const ref = useRef<HTMLAnchorElement>(null);
  
  useEffect(() => {
    if (isFocused && ref.current) {
      ref.current.focus();
    }
  }, [isFocused]);
  
  // SECURITY: Fail-closed - only authorized items reach this component
  // No need for 'unknown' handling since resolver filters them out
  const classNames = [
    'mega-menu-item',
    item.isCurrent ? 'current' : '',
  ].filter(Boolean).join(' ');
  
  // Show description in tooltip for additional context
  const tooltipText = item.description || item.label;
  
  return (
    <a
      ref={ref}
      href={item.path}
      role="menuitem"
      tabIndex={isFocused ? 0 : -1}
      className={classNames}
      title={tooltipText}
      aria-current={item.isCurrent ? 'page' : undefined}
      aria-describedby={item.description ? `desc-${item.id}` : undefined}
      onClick={(e) => {
        e.preventDefault();
        onItemClick(item);
      }}
      onFocus={onFocus}
    >
      {item.icon && <span className="mega-menu-item-icon">{isRegistryKey(item.icon) ? <ExperienceIcon iconKey={item.icon} size={16} /> : item.icon}</span>}
      <div className="mega-menu-item-content">
        <span className="mega-menu-item-label">{item.label}</span>
        {item.description && (
          <span id={`desc-${item.id}`} className="mega-menu-item-description">
            {item.description}
          </span>
        )}
      </div>
      {item.isCurrent && <span className="mega-menu-item-current-badge"><Check size={14} strokeWidth={2.5} aria-hidden="true" /></span>}
    </a>
  );
}

// =============================================================================
// MEGA MENU COLUMN
// =============================================================================

interface MegaMenuColumnProps {
  column: ResolvedMenuColumn;
  columnIndex: number;
  focusedItemIndex: number;
  onItemClick: (item: ResolvedMenuItem) => void;
  onItemFocus: (columnIndex: number, itemIndex: number) => void;
  isRevealed: boolean;
}

function MegaMenuColumn({ 
  column, 
  columnIndex, 
  focusedItemIndex, 
  onItemClick, 
  onItemFocus,
  isRevealed,
}: MegaMenuColumnProps) {
  return (
    <div 
      className={`mega-menu-column mega-menu-column-${columnIndex} ${isRevealed ? 'revealed' : 'revealing'}`}
      role="group"
      aria-labelledby={`column-${columnIndex}-header`}
    >
      <h3 id={`column-${columnIndex}-header`} className="mega-menu-column-header">
        {column.title}
      </h3>
      {column.items.map((item, itemIndex) => (
        <MegaMenuItem
          key={item.id}
          item={item}
          isFocused={focusedItemIndex === itemIndex}
          onItemClick={onItemClick}
          onFocus={() => onItemFocus(columnIndex, itemIndex)}
        />
      ))}
    </div>
  );
}

// =============================================================================
// MEGA MENU SKELETON
// =============================================================================

interface MegaMenuSkeletonProps {
  columnCount?: number;
}

export function MegaMenuSkeleton({ columnCount = 4 }: MegaMenuSkeletonProps) {
  return (
    <div className="mega-menu-skeleton">
      {/* Search skeleton */}
      <div className="mega-menu-skeleton-search" />
      
      {/* Recent section skeleton */}
      <div className="mega-menu-skeleton-recent">
        {Array(4).fill(null).map((_, i) => (
          <div key={i} className="mega-menu-skeleton-recent-item" style={{ animationDelay: `${i * 50}ms` }} />
        ))}
      </div>
      
      {/* Columns skeleton */}
      <div className={`mega-menu-skeleton-columns mega-menu-columns-${columnCount}`}>
        {Array(columnCount).fill(null).map((_, colIdx) => (
          <div key={colIdx} className="mega-menu-skeleton-column">
            <div className="mega-menu-skeleton-header" style={{ animationDelay: `${colIdx * 100}ms` }} />
            {Array(5).fill(null).map((_, itemIdx) => (
              <div 
                key={itemIdx} 
                className="mega-menu-skeleton-item" 
                style={{ animationDelay: `${(colIdx * 100) + (itemIdx * 50)}ms` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// RECENT PLUGINS SECTION
// =============================================================================

interface RecentSectionProps {
  recentIds: string[];
  allItems: ResolvedMenuItem[];
  onItemClick: (item: ResolvedMenuItem) => void;
}

function RecentSection({ recentIds, allItems, onItemClick }: RecentSectionProps) {
  if (recentIds.length === 0) return null;
  
  // Map recent IDs to actual items
  const itemMap = useMemo(() => 
    new Map(allItems.map(item => [item.id, item])),
    [allItems]
  );
  
  const recentItems = recentIds
    .map(id => itemMap.get(id))
    .filter((item): item is ResolvedMenuItem => item !== undefined);
  
  if (recentItems.length === 0) return null;
  
  return (
    <div className="mega-menu-recent">
      <h4 className="mega-menu-recent-title">Recent</h4>
      <div className="mega-menu-recent-items">
        {recentItems.map(item => (
          <button
            key={item.id}
            className="mega-menu-recent-item"
            onClick={() => onItemClick(item)}
          >
            {item.icon && <span>{isRegistryKey(item.icon) ? <ExperienceIcon iconKey={item.icon} size={14} /> : item.icon}</span>}
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// KEYBOARD HINTS FOOTER
// =============================================================================

function KeyboardHintsFooter() {
  return (
    <div className="mega-menu-footer">
      <span className="mega-menu-hint">
        <kbd>↑</kbd><kbd>↓</kbd> Navigate
      </span>
      <span className="mega-menu-hint">
        <kbd>←</kbd><kbd>→</kbd> Columns
      </span>
      <span className="mega-menu-hint">
        <kbd>Enter</kbd> Select
      </span>
      <span className="mega-menu-hint">
        <kbd>/</kbd> Search
      </span>
      <span className="mega-menu-hint">
        <kbd>Esc</kbd> Close
      </span>
    </div>
  );
}

// =============================================================================
// NO RESULTS
// =============================================================================

function NoResults({ query }: { query: string }) {
  return (
    <div className="mega-menu-no-results">
      <div className="mega-menu-no-results-icon"><Search size={24} aria-hidden /></div>
      <div className="mega-menu-no-results-text">
        No plugins found matching "{query}"
      </div>
    </div>
  );
}

// =============================================================================
// MEGA MENU PANEL
// =============================================================================

interface MegaMenuPanelProps {
  group: ResolvedMenuGroup;
  position: { left: number; top: number };
  recentPluginIds: string[];
  onClose: () => void;
  onItemClick: (item: ResolvedMenuItem) => void;
  loading?: boolean;
  globalSearchItems?: LauncherSearchItem[];
}

export const MegaMenuPanel = React.forwardRef<HTMLDivElement, MegaMenuPanelProps>(
  ({ group, position, recentPluginIds, onClose, onItemClick, loading, globalSearchItems }, ref) => {
    const panelInnerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedColumn, setFocusedColumn] = useState(0);
    const [focusedItem, setFocusedItem] = useState(-1); // -1 = no item focused (user is in search)
    const [revealed, setRevealed] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isNavigating, setIsNavigating] = useState(false); // true when user uses arrow keys to navigate
    
    // Get all items for recent section
    const allItems = useMemo(() => {
      if (group.type === 'mega' && group.columns) {
        return group.columns.flatMap(col => col.items);
      }
      return group.items ?? [];
    }, [group]);
    
    // Magnum: Fuzzy filter with relevance sorting
    const filteredGroup = useMemo(() => {
      if (!searchQuery.trim()) return group;
      
      if (group.type === 'mega' && group.columns) {
        const filteredColumns = group.columns
          .map(col => ({
            ...col,
            items: fuzzyFilterItems(col.items, searchQuery)
          }))
          .filter(col => col.items.length > 0);
        
        return filteredColumns.length > 0
          ? { ...group, columns: filteredColumns }
          : { ...group, columns: [] };
      }
      
      if (group.type === 'dropdown' && group.items) {
        const filteredItems = fuzzyFilterItems(group.items, searchQuery);
        return { ...group, items: filteredItems };
      }
      
      return group;
    }, [group, searchQuery]);
    
    // Get columns/items for navigation
    const columns = filteredGroup.type === 'mega' ? (filteredGroup.columns ?? []) : [];
    const items = filteredGroup.type === 'dropdown' ? (filteredGroup.items ?? []) : [];
    const hasContent = columns.length > 0 || items.length > 0;

    // Magnum: Count total results for screen reader
    const totalResults = useMemo(() => {
      if (columns.length > 0) {
        return columns.reduce((sum, col) => sum + col.items.length, 0);
      }
      return items.length;
    }, [columns, items]);
    
    // Reveal animation
    useEffect(() => {
      if (!loading) {
        requestAnimationFrame(() => setRevealed(true));
      }
    }, [loading]);
    
    // Focus search on open
    useEffect(() => {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }, []);
    
    // Magnum: Focus trap - keep focus inside panel
    useEffect(() => {
      const panel = panelInnerRef.current;
      if (!panel) return;
      
      const focusableElements = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      
      function handleFocusTrap(e: KeyboardEvent) {
        if (e.key !== 'Tab') return;
        
        if (e.shiftKey) {
          // Shift+Tab: if on first element, wrap to last
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable?.focus();
          }
        } else {
          // Tab: if on last element, wrap to first
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable?.focus();
          }
        }
      }
      
      panel.addEventListener('keydown', handleFocusTrap);
      return () => panel.removeEventListener('keydown', handleFocusTrap);
    }, [revealed, hasContent]);
    
    // Magnum: Animated close
    const handleAnimatedClose = useCallback(() => {
      setIsClosing(true);
      setTimeout(() => {
        onClose();
      }, 150); // Match CSS transition duration
    }, [onClose]);
    
    // Keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
      const isSearchFocused = e.target === searchInputRef.current;
      const columnItems = columns[focusedColumn]?.items ?? items;
      const columnCount = columns.length || 1;
      
      // Allow normal input behavior when search is focused
      // Only handle Escape and Enter from search
      if (isSearchFocused) {
        switch (e.key) {
          case 'Escape':
            e.preventDefault();
            handleAnimatedClose();
            break;
          case 'Enter':
            // If there's exactly one result, select it
            if (searchQuery && totalResults === 1) {
              e.preventDefault();
              const firstItem = columns[0]?.items[0] ?? items[0];
              if (firstItem) {
                onItemClick(firstItem);
              }
            }
            break;
          case 'ArrowDown':
            // Move focus from search to first menu item
            e.preventDefault();
            searchInputRef.current?.blur();
            setIsNavigating(true);
            setFocusedColumn(0);
            setFocusedItem(0);
            break;
        }
        return; // Let all other keys work normally in search input
      }
      
      // Panel keyboard navigation (when NOT in search input)
      switch (e.key) {
        case 'ArrowRight':
          if (columns.length > 0) {
            e.preventDefault();
            setFocusedColumn(c => Math.min(c + 1, columns.length - 1));
            setFocusedItem(0);
          }
          break;
          
        case 'ArrowLeft':
          if (columns.length > 0) {
            e.preventDefault();
            setFocusedColumn(c => Math.max(c - 1, 0));
            setFocusedItem(0);
          }
          break;
          
        case 'ArrowDown':
          e.preventDefault();
          setFocusedItem(i => Math.min(i + 1, columnItems.length - 1));
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          // If at top of list, return focus to search
          if (focusedItem === 0) {
            searchInputRef.current?.focus();
          } else {
            setFocusedItem(i => Math.max(i - 1, 0));
          }
          break;
          
        case 'Enter':
          e.preventDefault();
          const item = columnItems[focusedItem];
          if (item) {
            onItemClick(item);
          }
          break;

        case ' ':
          // Space should activate the focused item like Enter
          e.preventDefault();
          const spaceItem = columnItems[focusedItem];
          if (spaceItem) {
            onItemClick(spaceItem);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          handleAnimatedClose();
          break;
          
        case 'Home':
          e.preventDefault();
          setFocusedItem(0);
          break;
          
        case 'End':
          e.preventDefault();
          setFocusedItem(columnItems.length - 1);
          break;
          
        case '/':
          // Focus search
          e.preventDefault();
          searchInputRef.current?.focus();
          break;
          
        default:
          // Match release notes behavior: typing letters starts search filtering
          if (
            e.key.length === 1 &&
            /[a-z]/i.test(e.key) &&
            !e.altKey &&
            !e.ctrlKey &&
            !e.metaKey
          ) {
            e.preventDefault();
            setSearchQuery(prev => `${prev}${e.key}`);
            setIsNavigating(false);
            setFocusedItem(-1);
            searchInputRef.current?.focus();
          }
      }
    }, [columns, items, focusedColumn, focusedItem, handleAnimatedClose, onItemClick, searchQuery, totalResults]);
    
    // Calculate panel width - smart sizing for both mega and dropdown types
    // For dropdowns with many items, create virtual columns to prevent excessive height
    const effectiveColumnCount = useMemo(() => {
      if (columns.length > 0) {
        // Mega menu: use actual column count
        return Math.min(columns.length, 5);
      }
      // Dropdown with many items: split into virtual columns to reduce height
      const itemCount = items.length;
      if (itemCount > 12) return 3; // 3 columns for 13+ items
      if (itemCount > 6) return 2;  // 2 columns for 7-12 items
      return 1; // Single column for 6 or fewer
    }, [columns.length, items.length]);
    
    const panelWidth = Math.min(
      effectiveColumnCount * 280 + 40, // 280px per column + padding
      window.innerWidth - 40
    );
    
    // Adjust position to stay in viewport
    const adjustedPosition = useMemo(() => {
      const maxLeft = window.innerWidth - panelWidth - 20;
      return {
        left: Math.max(20, Math.min(position.left, maxLeft)),
        top: position.top,
      };
    }, [position, panelWidth]);
    
    // Magnum: Panel class includes closing state for animation
    const panelClasses = [
      'mega-menu-panel',
      isClosing ? 'closing' : '',
    ].filter(Boolean).join(' ');
    
    return (
      <div
        ref={(node) => {
          // Combine refs
          (panelInnerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        role="menu"
        aria-label={`${group.group} menu`}
        className={panelClasses}
        style={{
          left: adjustedPosition.left,
          top: adjustedPosition.top,
          width: panelWidth,
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="mega-menu-header">
          <span className="mega-menu-title">
            {group.icon && <span>{isRegistryKey(group.icon) ? <ExperienceIcon iconKey={group.icon} size={18} /> : group.icon}</span>}
            {group.group}
          </span>
          <button
            className="mega-menu-close"
            onClick={handleAnimatedClose}
            aria-label="Close menu"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        
        {/* Search */}
        <div className="mega-menu-search">
          <div className="mega-menu-search-wrapper">
            <span className="mega-menu-search-icon"><Search size={16} aria-hidden /></span>
            <input
              ref={searchInputRef}
              type="text"
              className="mega-menu-search-input"
              placeholder="Search plugins..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setIsNavigating(false); // Reset navigation mode when typing
                setFocusedItem(-1); // No item focused while typing
              }}
              onKeyDown={e => {
                // Stop propagation for typing keys to prevent type-ahead interference
                // Only let Escape, Enter, ArrowDown bubble up to panel handler
                if (!['Escape', 'Enter', 'ArrowDown'].includes(e.key)) {
                  e.stopPropagation();
                }
              }}
              aria-label="Search plugins"
              aria-describedby="search-results-status"
            />
            <span className="mega-menu-search-hint">/</span>
          </div>
          {/* Magnum: Screen reader live region for search results */}
          <div
            id="search-results-status"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mega-menu-sr-only"
          >
            {searchQuery && (
              totalResults === 0
                ? 'No plugins found'
                : `${totalResults} plugin${totalResults === 1 ? '' : 's'} found`
            )}
          </div>
        </div>
        
        {/* Loading State */}
        {loading && <MegaMenuSkeleton columnCount={effectiveColumnCount} />}
        
        {/* Content */}
        {!loading && (
          <>
            {/* Recent Section */}
            {!searchQuery && (
              <RecentSection
                recentIds={recentPluginIds}
                allItems={allItems}
                onItemClick={onItemClick}
              />
            )}
            
            {/* No Results in this menu - but show global matches */}
            {!hasContent && searchQuery && !globalSearchItems?.length && <NoResults query={searchQuery} />}
            
            {/* Mega Menu Columns */}
            {filteredGroup.type === 'mega' && columns.length > 0 && (
              <div className={`mega-menu-columns mega-menu-columns-${effectiveColumnCount}`}>
                {columns.map((column, colIdx) => (
                  <MegaMenuColumn
                    key={column.title}
                    column={column}
                    columnIndex={colIdx}
                    focusedItemIndex={isNavigating && focusedColumn === colIdx ? focusedItem : -1}
                    onItemClick={onItemClick}
                    onItemFocus={(ci, ii) => {
                      setIsNavigating(true);
                      setFocusedColumn(ci);
                      setFocusedItem(ii);
                    }}
                    isRevealed={revealed}
                  />
                ))}
              </div>
            )}
            
            {/* Simple Dropdown Items - split into virtual columns if many items */}
            {filteredGroup.type === 'dropdown' && items.length > 0 && (
              <div className={`mega-menu-columns mega-menu-columns-${effectiveColumnCount}`}>
                {effectiveColumnCount === 1 ? (
                  // Single column
                  <div className="mega-menu-column revealed">
                    {items.map((item, idx) => (
                      <MegaMenuItem
                        key={item.id}
                        item={item}
                        isFocused={isNavigating && focusedItem === idx}
                        onItemClick={onItemClick}
                        onFocus={() => {
                          setIsNavigating(true);
                          setFocusedItem(idx);
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  // Split into virtual columns for better layout
                  (() => {
                    const itemsPerColumn = Math.ceil(items.length / effectiveColumnCount);
                    const virtualColumns: ResolvedMenuItem[][] = [];
                    for (let i = 0; i < effectiveColumnCount; i++) {
                      virtualColumns.push(items.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn));
                    }
                    return virtualColumns.map((colItems, colIdx) => (
                      <div 
                        key={`virtual-col-${colIdx}`}
                        className={`mega-menu-column mega-menu-column-${colIdx} revealed`}
                      >
                        {colItems.map((item, itemIdx) => {
                          const globalIdx = colIdx * itemsPerColumn + itemIdx;
                          return (
                            <MegaMenuItem
                              key={item.id}
                              item={item}
                              isFocused={isNavigating && focusedColumn === colIdx && focusedItem === itemIdx}
                              onItemClick={onItemClick}
                              onFocus={() => {
                                setIsNavigating(true);
                                setFocusedColumn(colIdx);
                                setFocusedItem(itemIdx);
                              }}
                            />
                          );
                        })}
                      </div>
                    ));
                  })()
                )}
              </div>
            )}
          </>
        )}

        {/* Global matches from outside this menu */}
        {searchQuery && globalSearchItems && globalSearchItems.length > 0 && (() => {
          const localIds = new Set(
            (group.type === 'mega' && group.columns
              ? group.columns.flatMap(c => c.items)
              : group.items ?? []
            ).map(i => i.id)
          );
          const outsideMatches = rankLauncherResults(
            globalSearchItems.filter(gi => !localIds.has(gi.id)),
            searchQuery,
            { currentGroup: group.group }
          );
          const topOutside = outsideMatches.bestMatch;
          if (topOutside.length === 0) return null;
          return (
            <div className="mega-menu-global-matches">
              <h4 className="mega-menu-global-title">
                {hasContent ? 'Other matches' : 'Best matches'}
              </h4>
              {topOutside.map(gi => (
                <a
                  key={gi.id}
                  href={gi.route}
                  className="mega-menu-item"
                  onClick={(e) => {
                    e.preventDefault();
                    trackMegaMenuOutsideResult(gi.id, group.group, searchQuery.length);
                    onItemClick({ id: gi.id, label: gi.title, path: gi.route, icon: gi.icon, accessible: true });
                  }}
                >
                  {gi.icon && <span className="mega-menu-item-icon">{isRegistryKey(gi.icon) ? <ExperienceIcon iconKey={gi.icon} size={16} /> : gi.icon}</span>}
                  <div className="mega-menu-item-content">
                    <span className="mega-menu-item-label">{gi.title}</span>
                    {gi.subtitle && (
                      <span className="mega-menu-item-description">{gi.subtitle}</span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          );
        })()}
        
        {/* Keyboard Hints */}
        <KeyboardHintsFooter />
      </div>
    );
  }
);

MegaMenuPanel.displayName = 'MegaMenuPanel';

// =============================================================================
// MAIN NAV MEGA MENU COMPONENT
// =============================================================================

interface NavMegaMenuProps {
  group: ResolvedMenuGroup;
  recentPluginIds: string[];
  onPluginClick: (pluginId: string) => void;
  loading?: boolean;
  globalSearchItems?: LauncherSearchItem[];
}

export function NavMegaMenu({ group, recentPluginIds, onPluginClick, loading, globalSearchItems }: NavMegaMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  
  // Close handler - returns focus to trigger
  const handleClose = useCallback(() => {
    setIsOpen(false);
    // Return focus to trigger (Magnum: proper focus management)
    requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  }, []);
  
  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        panelRef.current && !panelRef.current.contains(target)
      ) {
        handleClose();
      }
    }
    
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);
  
  // Close on Escape (backup - also handled in panel)
  useEffect(() => {
    if (!isOpen) return;
    
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        handleClose();
      }
    }
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);
  
  // Calculate position when opening
  const handleToggle = useCallback(() => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        left: Math.max(20, rect.left - 100), // Offset to center-ish under trigger
        top: rect.bottom + 8,
      });
    }
    setIsOpen(prev => !prev);
  }, [isOpen]);
  
  // Handle item click
  const handleItemClick = useCallback((item: ResolvedMenuItem) => {
    onPluginClick(item.id);
    handleClose();
    // Navigate after close
    setTimeout(() => {
      window.location.href = item.path;
    }, 50);
  }, [onPluginClick, handleClose]);
  
  return (
    <>
      <MegaMenuTrigger
        ref={triggerRef}
        label={group.group}
        icon={group.icon}
        isOpen={isOpen}
        onClick={handleToggle}
      />
      
      {isOpen && ReactDOM.createPortal(
        <MegaMenuPanel
          ref={panelRef}
          group={group}
          position={position}
          recentPluginIds={recentPluginIds}
          onClose={handleClose}
          onItemClick={handleItemClick}
          loading={loading}
          globalSearchItems={globalSearchItems}
        />,
        document.body
      )}
    </>
  );
}

export default NavMegaMenu;
